import {
    accessDelegationListQueryAtom,
    type AccessDelegationWithType,
} from "@/modules/access/delegation/accessDelegationApi.ts";
import type { AccessImpersonationWithType } from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { apiTokenListQueryAtom, type ApiTokenWithType } from "@/modules/access/token/apiTokenApi.ts";
import { resourceTypesEnabledAtom, type ResourceType } from "@/modules/api/resourceTypes.tsx";
import { allVmQueryAtom, type VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { allDiskWithPricesQueryAtom, type DiskWithPrice } from "@/modules/disks/disksListQuery.ts";
import { metalQueryAtom, type MetalListItemWithType } from "@/modules/metal/metalQuery.ts";
import { allFirewallQueryAtom, type FirewallWithType } from "@/modules/network/firewall/firewallApi.ts";
import { allIpQueryAtom, type IpAddressWithType } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { allLbsQueryAtom, type LoadBalancerWithType } from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import { allVpcsQueryAtom, type VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import { allServicesQueryAtom, type ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import type { BucketWithType } from "@/modules/storage/bucket/bucketApi.ts";
import { bucketListQueryAtom } from "@/modules/storage/bucket/bucketQuery.ts";
import { mergeQueries, type QResult } from "@/utils/query/mergeQueries.ts";
import { atom, type Atom } from "jotai/vanilla";
import { map, mapValues, omitBy, pick, pipe, values } from "remeda";

/** Any resource type that can be shown in the "Recent" block */
export type RecentResource =
    | ApiTokenWithType
    | AccessDelegationWithType
    | AccessImpersonationWithType
    | IpAddressWithType
    | LoadBalancerWithType
    | VpcWithType
    | VirtualMachineLoc
    | ManagedServiceLoc
    | BucketWithType
    | MetalListItemWithType
    | FirewallWithType
    | DiskWithPrice;

type ItemsResult = Map<unknown, RecentResource>;
type QueryAtom = Atom<QResult<ItemsResult>>;

/** Map from resource type to its query atom */
const resourceTypeToQuery = {
    virtual_machine: allVmQueryAtom,
    managed_service: allServicesQueryAtom,
    bucket: bucketListQueryAtom,
    vpc: allVpcsQueryAtom,
    ip_address: allIpQueryAtom,
    load_balancer: allLbsQueryAtom,
    api_token: apiTokenListQueryAtom,
    access_delegation: accessDelegationListQueryAtom,
    bare_metal: metalQueryAtom,
    // Not visible except in profile
    access_impersonation: undefined,
    // Listed in separate dashboard block
    billing_account: undefined,
    firewall: allFirewallQueryAtom,
    disk: allDiskWithPricesQueryAtom,
    storage_account: undefined, // TODO: Add this (WAR-5880)
} satisfies Record<ResourceType, QueryAtom | undefined>;

const enabledQueriesAtom = atom((get) => {
    const typeEnabled = get(resourceTypesEnabledAtom);
    return pipe(
        resourceTypeToQuery,
        // Filter non-existent queries and disabled types
        omitBy((atom, type) => atom == null || !typeEnabled[type]),
    );
});

/**
 * All resources (used for dashboard "Recent" block).
 *
 * NB: Only queries enabled resource types to prevent query spam in "lightweight" deployments (eg. bare metal).
 */
export const allResourcesAtom = atom((get) => {
    const enabledTypeQueries = pipe(
        get(enabledQueriesAtom),
        // NB: Must explicitly declare `queryAtom` type here, otherwise TypeScript gets confused since it is a union of all the different query types.
        mapValues((queryAtom: QueryAtom) => get(queryAtom)),
    );

    return mergeQueries(enabledTypeQueries, (maps) =>
        // Flatten the maps into a single array
        maps.flatMap((m: Map<unknown, RecentResource>) => [...m.values()]),
    );
});

/** User-created resources (used to show banner to create something) */
const realResourceQueriesAtom = atom((get): QResult<ItemsResult>[] =>
    pipe(
        get(enabledQueriesAtom),
        // Only check resource types that are considered "real" (eg. charged for)
        pick(["virtual_machine", "managed_service", "bucket", "vpc", "ip_address", "load_balancer"]),
        values(),
        // NB: (see comment above for `QueryAtom`)
        map((atom: QueryAtom) => get(atom)),
    ),
);

// Only export a boolean instead of a query to prevent unnecessary re-renders (an updating `QueryResult` keeping
// the same `data` will still always cause a re-render, because it is an object and Jotai uses `Object.is` for
// comparison).
const _hasAnyResourcesAtom = atom((get) => get(realResourceQueriesAtom).some((m) => (m.data?.size ?? 0) > 0));

export const hasNoResourcesAtom = atom((get) =>
    // Must be fully loaded to say there are no resources, otherwise we might show a "no resources" state while loading
    get(realResourceQueriesAtom).every((m) => !m.isPending && m.data?.size === 0),
);
